import {
  FIELD_TYPES,
  ACTIONS,
  CHARTS,
  RESULT_TYPES,
} from "@/utils/calculatorSchema";

import {
  calculateLoanEligibilityChecker,
  calculateLoanSummary,
  generateAmortizationSchedule,
  generateMonthlySchedule,
  getLoanBreakdownData,
  getOutstandingBalanceData,
  getPrincipalInterestTrend,
  formatAxisCurrency,
  getEmiCapacityData,
  getPropertyFinancingData,
  formatCurrency,
} from "@/utils/finance";

import {
  Wallet,
  CreditCard,
  Percent,
  Landmark,
  Home,
  BadgeCheck,
} from "lucide-react";

import InsightCard from "@/components/calculator/InsightCard";

import DataTable from "@/components/calculator/DataTable";

const eligibilityConfig = {
  id: "loan-eligibility-checker",

  shortTitle: "Eligibility",

  title: "Home Loan Eligibility Checker",

  subtitle:
    "Evaluate a home loan proposal against lender policy using FOIR, LTV, repayment capacity, and processing charges.",

  description:
    "Check whether a proposed home loan meets lender eligibility criteria based on income, existing obligations, property value, FOIR, LTV, and processing fees.",

  icon: BadgeCheck,

  keywords: [
    "Loan Eligibility",
    "Home Loan",
    "Mortgage",
    "FOIR",
    "Maximum Loan",
    "Eligibility Calculator",
  ],

  fields: [
    {
      key: "monthlyIncome",
      label: "Monthly Income",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 100000,
      min: 10000,
      max: 10000000,
      step: 1000,
    },

    {
      key: "existingEmis",
      label: "Existing Monthly EMIs",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 0,
      min: 0,
      max: 1000000,
      step: 1000,
    },

    {
      key: "propertyValue",
      label: "Property Value",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 10000000,
      min: 100000,
      max: 500000000,
      step: 100000,
    },

    {
      key: "proposedLoanAmount",
      label: "Proposed Loan Amount",
      type: FIELD_TYPES.CURRENCY,
      defaultValue: 8000000,
      min: 100000,
      max: 500000000,
      step: 100000,
    },

    {
      key: "annualRate",
      label: "Interest Rate",
      type: FIELD_TYPES.PERCENT,
      defaultValue: 8.5,
      min: 1,
      max: 20,
      step: 0.05,
    },

    {
      key: "tenureYears",
      label: "Loan Tenure",
      type: FIELD_TYPES.NUMBER,
      suffix: "Years",
      defaultValue: 20,
      min: 1,
      max: 40,
      step: 1,
    },

    {
      key: "allowedFoir",
      label: "Allowed FOIR",
      type: FIELD_TYPES.PERCENT,
      defaultValue: 60,
      min: 30,
      max: 80,
      step: 1,
    },

    {
      key: "allowedLtv",
      label: "Allowed LTV",
      type: FIELD_TYPES.PERCENT,
      defaultValue: 80,
      min: 50,
      max: 90,
      step: 1,
    },

    {
      key: "processingFee",
      label: "Processing Fee",
      type: FIELD_TYPES.PERCENT,
      input: "slider",
      defaultValue: 0.5,
      min: 0,
      max: 5,
      step: 0.05,
    },
  ],

  summaryCards: [
    {
      title: "Eligible Loan",
      key: "eligibleLoan",
      type: RESULT_TYPES.CURRENCY,
      description: "Maximum loan supported by your repayment capacity.",
      icon: Wallet,
    },

    {
      title: "Proposed EMI",
      key: "proposedEmi",
      type: RESULT_TYPES.CURRENCY,
      description: "Monthly EMI for the requested loan amount.",
      icon: CreditCard,
    },

    {
      title: "FOIR",
      key: "foir",
      type: RESULT_TYPES.PERCENT,
      description:
        "Fixed Obligation to Income Ratio after including the proposed EMI.",
      icon: Percent,
    },

    {
      title: "LTV",
      key: "ltv",
      type: RESULT_TYPES.PERCENT,
      description: "Loan-to-Value ratio based on the property value.",
      icon: Home,
    },

    {
      title: "Net Disbursal",
      key: "netDisbursal",
      type: RESULT_TYPES.CURRENCY,
      description: "Loan amount after deducting the processing fee.",
      icon: Landmark,
    },

    {
      title: "Down Payment",
      key: "downPayment",
      type: RESULT_TYPES.CURRENCY,
      description: "Amount to be contributed by the borrower.",
      icon: Wallet,
    },
  ],

  charts: [
    {
      id: "loan-breakdown",

      type: CHARTS.PIE,

      title: "Loan Breakdown",

      resultsKey: "loanBreakdown",

      dataKey: "value",

      nameKey: "name",

      innerRadius: 70,

      outerRadius: 110,

      minWidth: 580,
    },

    {
      id: "outstanding-balance",

      type: CHARTS.LINE,

      title: "Outstanding Balance",

      resultsKey: "outstandingBalance",

      minWidth: 580,

      xAxis: {
        dataKey: "year",
      },

      yAxis: {
        tickFormatter: formatAxisCurrency,
      },

      lines: [
        {
          dataKey: "balance",
        },
      ],
    },

    {
      id: "principal-interest",

      type: CHARTS.AREA,

      title: "Principal vs Interest",

      resultsKey: "principalInterestTrend",

      minWidth: 580,

      xAxis: {
        dataKey: "year",
      },

      yAxis: {
        tickFormatter: formatAxisCurrency,
      },

      areas: [
        {
          dataKey: "principal",
          stackId: "1",
        },
        {
          dataKey: "interest",
          stackId: "1",
        },
      ],
    },

    {
      id: "emi-capacity",
      title: "EMI Capacity Breakdown",
      description: "Monthly repayment capacity based on lender FOIR policy.",
      type: CHARTS.PIE,
      resultsKey: "emiCapacity",
    },

    {
      id: "property-financing",
      title: "Property Financing",
      description:
        "Distribution between the proposed loan amount and required down payment.",
      type: CHARTS.PIE,
      resultsKey: "propertyFinancing",
    },
  ],

  tables: [
    {
      title: "Yearly Schedule",

      component: DataTable,

      dataKey: "amortization",

      columns: [
        {
          key: "year",
          label: "Year",
          type: "number",
          sortable: true,
        },
        {
          key: "emi",
          label: "EMI Paid",
          type: "currency",
          sortable: true,
        },
        {
          key: "principal",
          label: "Principal",
          type: "currency",
          sortable: true,
        },
        {
          key: "interest",
          label: "Interest",
          type: "currency",
          sortable: true,
        },
        {
          key: "balance",
          label: "Outstanding",
          type: "currency",
          sortable: true,
        },
      ],
    },

    {
      title: "Monthly Schedule",

      component: DataTable,

      dataKey: "monthlySchedule",

      columns: [
        {
          key: "month",
          label: "Month",
          type: "number",
          sortable: true,
        },
        {
          key: "emi",
          label: "EMI",
          type: "currency",
          sortable: true,
        },
        {
          key: "principal",
          label: "Principal",
          type: "currency",
          sortable: true,
        },
        {
          key: "interest",
          label: "Interest",
          type: "currency",
          sortable: true,
        },
        {
          key: "balance",
          label: "Balance",
          type: "currency",
          sortable: true,
        },
      ],
    },
  ],

  actions: [ACTIONS.RESET, ACTIONS.COPY, ACTIONS.EXCEL],

  calculate(values) {
    const summary = calculateLoanEligibilityChecker(values);

    const loanSummary = calculateLoanSummary({
      principal: summary.eligibleLoan,
      annualRate: values.annualRate,
      tenureYears: values.tenureYears,
    });

    const amortizationSchedule = generateAmortizationSchedule({
      principal: summary.eligibleLoan,
      annualRate: values.annualRate,
      tenureYears: values.tenureYears,
    });

    const monthlySchedule = generateMonthlySchedule({
      principal: summary.eligibleLoan,
      annualRate: values.annualRate,
      tenureYears: values.tenureYears,
    });

    return {
      summary,

      charts: {
        loanBreakdown: getLoanBreakdownData(loanSummary),

        outstandingBalance: getOutstandingBalanceData(amortizationSchedule),

        principalInterestTrend: getPrincipalInterestTrend(amortizationSchedule),

        emiCapacity: getEmiCapacityData(summary),

        propertyFinancing: getPropertyFinancingData(summary),
      },

      tables: {
        amortization: amortizationSchedule,
        monthlySchedule,
      },

      metadata: {
        generatedAt: new Date().toISOString(),
      },
    };
  },

  exportExcel: ({ results }) => ({
    "Yearly Schedule": results.tables.amortization ?? [],

    "Monthly Schedule": results.tables.monthlySchedule ?? [],
  }),

  renderInsights({ values, results }) {
    const { monthlyIncome, existingEmis } = values;

    const {
      eligibleLoan,
      proposedLoanAmount,
      proposedEmi,
      eligibleEmi,
      maximumEmi,
      foir,
      ltv,
      allowedFoir,
      allowedLtv,
      processingFeeAmount,
      netDisbursal,
      downPayment,
      approval,
      approvalReasons,
    } = results.summary;

    const utilisation =
      maximumEmi === 0 ? 0 : (existingEmis / maximumEmi) * 100;

    return (
      <div className="space-y-4">
        <InsightCard
          type={approval ? "success" : "warning"}
          title={
            approval ? "Loan Proposal Approved" : "Proposal Requires Review"
          }
        >
          {approval
            ? "Your proposed loan satisfies the selected FOIR and LTV policy limits."
            : `The proposal exceeds the following policy limit(s): ${approvalReasons.join(", ")}.`}
        </InsightCard>

        <InsightCard type="info" title="Eligible Loan Amount">
          Based on your monthly income of{" "}
          <strong>{formatCurrency(monthlyIncome)}</strong>, existing EMIs of{" "}
          <strong>{formatCurrency(existingEmis)}</strong>, and the selected loan
          terms, you may be eligible for a loan of{" "}
          <strong>{formatCurrency(eligibleLoan)}</strong>.
        </InsightCard>

        <InsightCard
          type={foir <= allowedFoir ? "success" : "warning"}
          title="FOIR Assessment"
        >
          Your projected FOIR is <strong>{foir.toFixed(2)}%</strong> against the
          allowed limit of <strong>{allowedFoir}%</strong>.
        </InsightCard>

        <InsightCard
          type={ltv <= allowedLtv ? "success" : "warning"}
          title="LTV Assessment"
        >
          Your proposed loan results in an LTV of{" "}
          <strong>{ltv.toFixed(2)}%</strong> against the allowed limit of{" "}
          <strong>{allowedLtv}%</strong>.
        </InsightCard>

        <InsightCard type="info" title="Loan Disbursal">
          A processing fee of{" "}
          <strong>{formatCurrency(processingFeeAmount)}</strong> will be
          deducted, resulting in a net disbursal of{" "}
          <strong>{formatCurrency(netDisbursal)}</strong>.
        </InsightCard>

        <InsightCard type="info" title="Down Payment Required">
          Based on the property value and requested loan amount, you will need a
          down payment of <strong>{formatCurrency(downPayment)}</strong>.
        </InsightCard>

        {existingEmis > 0 && (
          <InsightCard
            type={utilisation >= 70 ? "warning" : "info"}
            title="Existing EMI Commitments"
          >
            Your existing EMIs consume{" "}
            <strong>{utilisation.toFixed(0)}%</strong> of your maximum repayment
            capacity before considering this loan.
          </InsightCard>
        )}

        {!approval && approvalReasons.includes("FOIR") && (
          <InsightCard type="warning" title="Recommendation">
            Consider reducing your existing EMIs, increasing your monthly
            income, extending the loan tenure, or lowering the requested loan
            amount to improve FOIR.
          </InsightCard>
        )}

        {!approval && approvalReasons.includes("LTV") && (
          <InsightCard type="warning" title="Recommendation">
            Consider increasing your down payment or reducing the proposed loan
            amount to bring the LTV within the lender's policy.
          </InsightCard>
        )}
      </div>
    );
  },
};

export default eligibilityConfig;
